import type { ExperienceDocument, Theme } from '@personalizehq/schemas/ir';

// THE AUTHORING DOC — the mutable D1-side artifact (SPEC §2). It is NOT the wire bundle and is
// never sent to an SDK: `publish` compiles it. That split is what lets this shape churn weekly
// while no pinned binary notices, so nothing here needs to be conservative.
//
// It exists because `ExperienceDocument` is ONE surface + ONE root — there is no multi-step shape
// anywhere in the contract (p3-authoring-dashboard.md §1, gap 1). A two-step onboarding carousel
// is the canonical demo, so the authoring doc is `steps[]` from day one; the WIRE flow model
// lands in P4 with the compiler, before any binary pins.
//
// The SHAPE is defined once, in `@personalizehq/schemas/authoring`, because the doc crosses a trust
// boundary (`POST /api/experiences`) and the server has to validate it with zod. This import is
// type-only and erases at build, so this package stays zero-runtime-dep on zod.
export type { AdvanceRule, AuthoringDoc, Step } from '@personalizehq/schemas';

import { DOC_VERSION, resolveAuthoringBlock, type AuthoringAssetResolver, type AuthoringBlockNode, type AuthoringDoc, type Step } from '@personalizehq/schemas';

export function newId(): string {
  return crypto.randomUUID();
}

/** An empty column stack — the smallest legal root a step can have. */
export function emptyRoot(): AuthoringBlockNode {
  return {
    block: 'stack',
    id: newId(),
    direction: 'column',
    gap: 0,
    align: 'stretch',
    justify: 'start',
    children: [],
  };
}

export function createStep(overrides: Partial<Step> = {}): Step {
  return {
    id: newId(),
    name: 'Step 1',
    surface: 'modal',
    root: emptyRoot(),
    ...overrides,
  };
}

export function createDoc(theme: Theme, overrides: Partial<AuthoringDoc> = {}): AuthoringDoc {
  return {
    doc_version: DOC_VERSION,
    theme,
    steps: [createStep()],
    ...overrides,
  };
}

/** Doc-wide: the default `steps`, then every variant's. Step ids are UUIDs, unique across the whole
 *  doc, so this is unambiguous — and it is what makes the ACTIVE VARIANT derived state: focusing a
 *  variant's step id is all it takes for every op and the artboard to operate on that variant. */
export function findStep(doc: AuthoringDoc, stepId: string): Step | undefined {
  const inDefault = doc.steps.find((s) => s.id === stepId);
  if (inDefault) return inDefault;
  for (const variant of doc.variants ?? []) {
    const step = variant.steps.find((s) => s.id === stepId);
    if (step) return step;
  }
  return undefined;
}

/** The list a step lives in, and whose variant it is (`null` = the default steps). The step ops
 *  below resolve through this so "add/remove/move a step" always means "in the list you are in". */
export function owningStepList(
  doc: AuthoringDoc,
  stepId: string
): { steps: Step[]; variantKey: string | null } | undefined {
  if (doc.steps.some((s) => s.id === stepId)) return { steps: doc.steps, variantKey: null };
  for (const variant of doc.variants ?? []) {
    if (variant.steps.some((s) => s.id === stepId)) return { steps: variant.steps, variantKey: variant.key };
  }
  return undefined;
}

/** `null` (or the reserved `'default'` handled by callers) → the doc's own steps. Throws on an
 *  unknown key: silently returning the default would edit the wrong arm. */
export function stepsOf(doc: AuthoringDoc, variantKey: string | null): Step[] {
  if (variantKey === null) return doc.steps;
  const variant = (doc.variants ?? []).find((v) => v.key === variantKey);
  if (!variant) throw new Error(`stepsOf: unknown variant "${variantKey}"`);
  return variant.steps;
}

/** Which variant the focused step belongs to — `null` for the default. Unknown ids read as the
 *  default too: `reconcile` is about to move focus there anyway. */
export function activeVariantKey(doc: AuthoringDoc, focusedStepId: string): string | null {
  return owningStepList(doc, focusedStepId)?.variantKey ?? null;
}

/** What the layers tree calls a node: the author's name if they gave it one, else a summary derived
 *  from the block itself. The derivation matters more than the rename does — a 12-node modal is
 *  perfectly readable as "vertical stack / text: Welcome back / button: Continue", and an author who
 *  never renames anything should never see a wall of "stack, stack, stack". */
export function nodeLabel(node: AuthoringBlockNode, labels: AuthoringDoc['labels']): string {
  const named = labels?.[node.id]?.trim();
  if (named) return named;

  switch (node.block) {
    case 'stack':
      return node.direction === 'row' ? 'horizontal stack' : 'vertical stack';
    case 'text':
    case 'button': {
      const spans = node.block === 'text' ? node.spans : node.label;
      const first = spans.find((s) => s.kind === 'lit');
      const literal = first?.kind === 'lit' ? first.value.trim() : '';
      return literal ? `${node.block}: ${truncate(literal)}` : node.block;
    }
    case 'scroll':
      return `${node.axis} scroll`;
    case 'custom':
      return `custom: ${node.identifier}`;
    default:
      return node.block;
  }
}

function truncate(value: string): string {
  return value.length <= 24 ? value : `${value.slice(0, 23)}…`;
}

/** Rename in a draft. An empty name CLEARS the entry rather than storing `""` — otherwise the doc
 *  accumulates empty strings that the schema (min 1) would then reject at save. */
export function setLabel(doc: AuthoringDoc, nodeId: string, name: string): void {
  const trimmed = name.trim();
  if (!trimmed) {
    if (doc.labels) delete doc.labels[nodeId];
    return;
  }
  doc.labels ??= {};
  doc.labels[nodeId] = trimmed.slice(0, 80);
}

// The step ops. Like `tree.ts`, these MUTATE and are written to run inside an immer draft
// (`applyChange`), so the store turns each into a patch pair and undo comes for free.
//
// The bounds are `authoringDocSchema`'s, not opinions: steps is `.min(1).max(20)`. Enforcing them
// here means the editor cannot author a doc the server will reject at save.

export const MAX_STEPS = 20;

/** Splices into the named variant's list (`variantKey` null/omitted = the default steps). The
 *  bounds are PER LIST — `variantSchema.steps` carries the same `.min(1).max(20)` as the doc's. */
export function addStep(
  doc: AuthoringDoc,
  index?: number,
  overrides: Partial<Step> = {},
  variantKey: string | null = null
): Step {
  const steps = stepsOf(doc, variantKey);
  if (steps.length >= MAX_STEPS) throw new Error(`addStep: a step list may hold at most ${MAX_STEPS} steps`);
  const step = createStep({ name: `Step ${steps.length + 1}`, ...overrides });
  const at = index === undefined ? steps.length : clamp(index, steps.length);
  steps.splice(at, 0, step);
  return step;
}

export function removeStep(doc: AuthoringDoc, stepId: string): void {
  const owner = owningStepList(doc, stepId);
  if (!owner) throw new Error(`removeStep: unknown step ${stepId}`);
  // A list with zero steps is unsavable AND uncompilable — `stepToExperienceDocument` has nothing to
  // return. Refusing here is the difference between a disabled button and a broken document.
  if (owner.steps.length <= 1) throw new Error('removeStep: a step list must keep at least one step');
  owner.steps.splice(owner.steps.findIndex((s) => s.id === stepId), 1);
}

/** Reorders WITHIN the owning list — a step never moves between variants (that would silently
 *  rewrite two arms of an experiment in one gesture). */
export function moveStep(doc: AuthoringDoc, stepId: string, index: number): void {
  const owner = owningStepList(doc, stepId);
  if (!owner) throw new Error(`moveStep: unknown step ${stepId}`);
  const from = owner.steps.findIndex((s) => s.id === stepId);
  const [step] = owner.steps.splice(from, 1);
  owner.steps.splice(clamp(index, owner.steps.length), 0, step as Step);
}

function clamp(index: number, length: number): number {
  if (!Number.isFinite(index)) return length;
  return Math.max(0, Math.min(Math.trunc(index), length));
}

/** The seam to the renderer: `renderer-web`'s `mount()` takes an `ExperienceDocument`, so the canvas
 *  renders EXACTLY what the SDK will render — no editor-only preview path, ever. */
export function stepToExperienceDocument(
  doc: AuthoringDoc,
  stepId: string,
  resolveAsset?: AuthoringAssetResolver
): ExperienceDocument {
  const step = findStep(doc, stepId);
  if (!step) throw new Error(`unknown step: ${stepId}`);
  return {
    schema_version: 'v1',
    surface: step.surface,
    theme: doc.theme,
    root: resolveAuthoringBlock(step.root, resolveAsset),
  };
}
